import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'gatsby';
import { ChevronDown, Menu } from './icons';
import { SPECIALTY_PATHS } from '../data/specialtyDirectory';

// Desktop hover menus close after a short grace period, so moving the pointer
// from the trigger down into the menu (or briefly overshooting) never snaps it
// shut. Re-entering before the delay cancels the close.
const MENU_CLOSE_DELAY_MS = 250;

function useHoverMenu() {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  // Escape returns focus to the trigger; that focus must not reopen the menu.
  const suppressFocusOpen = useRef(false);
  const cancel = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => cancel, []);
  return {
    open,
    show: () => { cancel(); setOpen(true); },
    hideSoon: () => { cancel(); timer.current = setTimeout(() => setOpen(false), MENU_CLOSE_DELAY_MS); },
    hideNow: () => { cancel(); setOpen(false); },
    canOpenOnFocus: () => !suppressFocusOpen.current,
    closeToTrigger: (triggerRef) => {
      cancel();
      setOpen(false);
      suppressFocusOpen.current = true;
      triggerRef.current?.focus();
      suppressFocusOpen.current = false;
    },
  };
}

// The menu sits inside a positioning "bridge" that starts flush with the bottom
// of the trigger and uses padding (not margin) for the visual gap, so the
// pointer never leaves the hover area on its way down. Visibility flips only
// after the fade-out finishes, so the close animation can actually play.
function menuBridgeClass(open) {
  return `absolute right-0 top-full pt-4 w-64 z-50 ${
    open ? 'visible [transition:visibility_0s]' : 'invisible [transition:visibility_0s_linear_450ms]'
  }`;
}

function menuPanelClass(open) {
  return `rounded-2xl shadow-xl bg-surface/95 ring-1 ring-ink/10 backdrop-blur-xl transition origin-top-right ${
    open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
  }`;
}

export default function Navigation({ location }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const servicesMenu = useHoverMenu();
  const aboutMenu = useHoverMenu();
  const servicesDropdownOpen = servicesMenu.open;
  const aboutDropdownOpen = aboutMenu.open;
  // Opening one desktop menu closes the other immediately.
  const openServices = () => { aboutMenu.hideNow(); servicesMenu.show(); };
  const openAbout = () => { servicesMenu.hideNow(); aboutMenu.show(); };
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const pathname = location?.pathname || '/';
  const servicesTriggerRef = useRef(null);
  const aboutTriggerRef = useRef(null);

  const isActive = (path) => pathname === path;

  // Check if any about-related page is active
  const isAboutActive = () => {
    return pathname === '/about' ||
           pathname === '/our-approach' ||
           pathname === '/insurance-fees' ||
           pathname === '/coaching-and-spiritual-counseling' ||
           pathname === '/blog';
  };

  // Check if any services-related page is active. Specialty pages (the full
  // SPECIALTY_DIRECTORY) now live under How We Help, so any of them highlights it.
  const isServicesActive = () => {
    return pathname === '/how-we-help' ||
           pathname === '/individual-therapy' ||
           pathname === '/couples-relationship-therapy' ||
           pathname === '/online-therapy' ||
           pathname === '/start-therapy-sooner' ||
           SPECIALTY_PATHS.includes(pathname);
  };

  return (
    <nav className="site-nav fixed w-full z-40 transition duration-700 backdrop-blur-md border-b">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="font-serif text-xl md:text-2xl text-accent-display tracking-tight font-medium hover:text-accent-display transition-colors">
              Peace Love Wellness
            </Link>
          </div>

          <div className="hidden md:flex md:space-x-4 lg:space-x-10 items-center">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors border-b-2 pb-1 ${
                isActive('/') ? 'text-accent border-accent' : 'text-ink-muted hover:text-accent border-transparent transition-colors'
              }`}
            >
              Home
            </Link>
            <Link
              to="/our-clinicians"
              className={`text-sm font-medium transition-colors border-b-2 pb-1 ${
                isActive('/our-clinicians') ? 'text-accent border-accent' : 'text-ink-muted hover:text-accent border-transparent transition-colors'
              }`}
            >
              Find Your Therapist
            </Link>
            <div
              className="relative"
              onMouseEnter={openServices}
              onMouseLeave={servicesMenu.hideSoon}
              onFocus={() => { if (servicesMenu.canOpenOnFocus()) openServices(); }}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) servicesMenu.hideNow(); }}
              onKeyDown={(e) => { if (e.key === 'Escape') servicesMenu.closeToTrigger(servicesTriggerRef); }}
            >
              <Link
                ref={servicesTriggerRef}
                to="/how-we-help"
                aria-haspopup="true"
                aria-expanded={servicesDropdownOpen}
                className={`text-sm font-medium flex items-center transition-colors border-b-2 pb-1 ${
                  isServicesActive() ? 'text-accent border-accent' : 'text-ink-muted hover:text-accent border-transparent transition-colors'
                }`}
              >
                How We Help <ChevronDown className="ml-1 w-4 h-4 opacity-50" aria-hidden="true" />
              </Link>
              <div className={menuBridgeClass(servicesDropdownOpen)}>
              <div
                role="menu"
                aria-label="How We Help"
                className={menuPanelClass(servicesDropdownOpen)}
              >
                <div className="py-3 px-1">
                  <Link
                    role="menuitem"
                    to="/individual-therapy"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={servicesMenu.hideNow}
                  >
                    Individual Therapy
                  </Link>
                  <Link
                    role="menuitem"
                    to="/couples-relationship-therapy"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={servicesMenu.hideNow}
                  >
                    Couples & Relationship Therapy
                  </Link>
                  <Link
                    role="menuitem"
                    to="/online-therapy"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={servicesMenu.hideNow}
                  >
                    Online Therapy
                  </Link>
                  <Link
                    role="menuitem"
                    to="/start-therapy-sooner"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={servicesMenu.hideNow}
                  >
                    Work With a Supervised Clinician
                  </Link>
                  <div className="my-1 mx-4 border-t border-coral/15" />
                  <Link
                    role="menuitem"
                    to="/how-we-help#specialties"
                    className="block px-4 py-3 text-sm font-medium text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={servicesMenu.hideNow}
                  >
                    All Specialties & Concerns →
                  </Link>
                </div>
              </div>
              </div>
            </div>

            <div
              className="relative"
              onMouseEnter={openAbout}
              onMouseLeave={aboutMenu.hideSoon}
              onFocus={() => { if (aboutMenu.canOpenOnFocus()) openAbout(); }}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) aboutMenu.hideNow(); }}
              onKeyDown={(e) => { if (e.key === 'Escape') aboutMenu.closeToTrigger(aboutTriggerRef); }}
            >
              <Link
                ref={aboutTriggerRef}
                to="/about"
                aria-haspopup="true"
                aria-expanded={aboutDropdownOpen}
                className={`text-sm font-medium flex items-center transition-colors border-b-2 pb-1 ${
                  isAboutActive() ? 'text-accent border-accent' : 'text-ink-muted hover:text-accent border-transparent'
                }`}
              >
                About <ChevronDown className="ml-1 w-4 h-4 opacity-50" aria-hidden="true" />
              </Link>
              <div className={menuBridgeClass(aboutDropdownOpen)}>
              <div
                role="menu"
                aria-label="About"
                className={menuPanelClass(aboutDropdownOpen)}
              >
                <div className="py-3 px-1">
                  <Link
                    role="menuitem"
                    to="/our-approach"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={aboutMenu.hideNow}
                  >
                    Our Approach
                  </Link>
                  <Link
                    role="menuitem"
                    to="/insurance-fees"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={aboutMenu.hideNow}
                  >
                    Insurance & Fees
                  </Link>
                  <Link
                    role="menuitem"
                    to="/coaching-and-spiritual-counseling"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={aboutMenu.hideNow}
                  >
                    Coaching & Spiritual Counseling
                  </Link>
                  <Link
                    role="menuitem"
                    to="/blog"
                    className="block px-4 py-3 text-sm text-accent rounded-xl hover:bg-accent-fill hover:text-on-accent transition-colors"
                    onClick={aboutMenu.hideNow}
                  >
                    Blog
                  </Link>
                </div>
              </div>
              </div>
            </div>

            <Link to="/get-started" className="btn btn-primary btn-sm">
              Get Started
            </Link>
          </div>

          <div className="md:hidden flex items-center">
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen((open) => !open);
                setMobileServicesOpen(false);
                setMobileAboutOpen(false);
              }}
              className="text-accent p-2"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden bg-surface backdrop-blur-md absolute top-full w-full border-t border-line shadow-2xl`}
      >
        <div className="px-6 py-8 space-y-4">
          <Link
            to="/"
            className={`block text-lg font-serif ${isActive('/') ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/our-clinicians"
            className={`block text-lg font-serif ${isActive('/our-clinicians') ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Find Your Therapist
          </Link>
          <div>
            <div className="flex items-center justify-between">
              <Link
                to="/how-we-help"
                className={`block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How We Help
              </Link>
              <button
                type="button"
                onClick={() => setMobileServicesOpen((open) => !open)}
                aria-expanded={mobileServicesOpen}
                aria-controls="mobile-services-submenu"
                aria-label={mobileServicesOpen ? 'Collapse How We Help submenu' : 'Expand How We Help submenu'}
                className="p-2 -mr-2 text-accent"
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <div id="mobile-services-submenu" className={`${mobileServicesOpen ? 'block' : 'hidden'} mt-4 space-y-4`}>
              <Link
                to="/individual-therapy"
                className={`ml-8 block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Individual Therapy
              </Link>
              <Link
                to="/couples-relationship-therapy"
                className={`ml-8 block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Couples & Relationship Therapy
              </Link>
              <Link
                to="/online-therapy"
                className={`ml-8 block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Online Therapy
              </Link>
              <Link
                to="/start-therapy-sooner"
                className={`ml-8 block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Supervised Clinicians
              </Link>
              <Link
                to="/how-we-help#specialties"
                className={`ml-8 block text-lg font-serif ${isServicesActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                All Specialties & Concerns
              </Link>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Link
                to="/about"
                className={`block text-lg font-serif ${isAboutActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              <button
                type="button"
                onClick={() => setMobileAboutOpen((open) => !open)}
                aria-expanded={mobileAboutOpen}
                aria-controls="mobile-about-submenu"
                aria-label={mobileAboutOpen ? 'Collapse About submenu' : 'Expand About submenu'}
                className="p-2 -mr-2 text-accent"
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <div id="mobile-about-submenu" className={`${mobileAboutOpen ? 'block' : 'hidden'} mt-4 space-y-4`}>
              <Link
                to="/our-approach"
                className={`ml-8 block text-lg font-serif ${isAboutActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Our Approach
              </Link>
              <Link
                to="/insurance-fees"
                className={`ml-8 block text-lg font-serif ${isAboutActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Insurance & Fees
              </Link>
              <Link
                to="/coaching-and-spiritual-counseling"
                className={`ml-8 block text-lg font-serif ${isAboutActive() ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Coaching & Spiritual Counseling
              </Link>
              <Link
                to="/blog"
                className={`ml-8 block text-lg font-serif ${isActive('/blog') ? 'text-accent' : 'text-ink-muted hover:text-accent'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
            </div>
          </div>
          <Link
            to="/get-started"
            className="btn btn-primary btn-lg block w-full"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Get Started
          </Link>
        </div>
      </div>
    </nav>
  );
}
